// Castintech dashboard — utility helpers (TypeScript).
// Faz 9b PR3 — dashboard.html'den extract; pure functions, side-effect yok.
// Faz 12 PR-C — utils.js → utils.ts (signature'lar typed).
// Faz 13 — DOM type narrowing helper'lari ($, $$, asEl).

import { GEMS, MODEL_LABELS, type GemConfig } from './config.js';
import type { Agent } from '../types/api';

type DateLike = Date | string | number | null | undefined;

// Insan-okur zaman fark (Date|string → "az önce"|"5dk"|"3sa"|"2g").
export function ago(d: DateLike): string {
  if (!d) return '';
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60) return 'az önce';
  if (s < 3600) return Math.floor(s / 60) + 'dk';
  if (s < 86400) return Math.floor(s / 3600) + 'sa';
  return Math.floor(s / 86400) + 'g';
}

// Issue/agent status string → kullanici-yuezuene Turkce label.
export function statusLabel(s: string | null | undefined): string {
  const map: Record<string, string> = {
    todo: 'Bekliyor', in_progress: 'Devam', blocked: 'Bloklu',
    done: 'Tamamlandı', backlog: 'Backlog', open: 'Devam',
    completed: 'Tamamlandı', failed: 'Hata',
  };
  return map[(s || '').toLowerCase()] || s || '—';
}

// Agent status → CSS class adi (active/busy/error/idle).
// Faz 47.2 S3.A — Paperclip canonical statuses: idle/working/error/paused.
// `active` ve `running` legacy alias'lar (eski kod yolu için korundu).
// `working` artık `busy` map'ine yönlendirilir (audit'in "Çalışan" sayısı).
// `paused` idle gibi davranır (offline/disabled görsel — ayrı pill yok).
export function statusCls(s: string | null | undefined): 'active' | 'busy' | 'error' | 'idle' {
  const l = (s || '').toLowerCase();
  if (l === 'active' || l === 'running') return 'active';
  if (l === 'busy' || l === 'working') return 'busy';
  if (l === 'error') return 'error';
  return 'idle';
}

// Issue status normalizasyonu (paperclip cesitli isim varyantlari → kanban kolon adi).
export function normStatus(s: string | null | undefined): 'in_progress' | 'done' | 'todo' | 'blocked' | 'backlog' {
  const l = (s || '').toLowerCase();
  if (l === 'open' || l === 'in_progress') return 'in_progress';
  if (l === 'done' || l === 'closed' || l === 'completed') return 'done';
  if (l === 'todo') return 'todo';
  if (l === 'blocked') return 'blocked';
  return 'backlog';
}

// Görev dizilimi STABİL sıralama (yeniden→eskiye). Tie-break: id DESC.
// Aynı updated_at/created_at'e sahip görevlerin her refresh'te yer değiştirmesini
// (kart zıplaması) önler — backend ORDER BY ..., id DESC ile hizalı.
type TaskLike = { id?: string | null; updatedAt?: DateLike; updated_at?: DateLike; created_at?: DateLike };
export function compareTasksStable(a: TaskLike, b: TaskLike): number {
  // `?? 0` zinciri null/undefined'ı runtime'da eler; cast'i de daraltıyoruz
  // (DateLike null/undefined içerir → new Date() overload'u kabul etmez, TS2769).
  const at = new Date((a.updatedAt ?? a.updated_at ?? a.created_at ?? 0) as string | number | Date).getTime();
  const bt = new Date((b.updatedAt ?? b.updated_at ?? b.created_at ?? 0) as string | number | Date).getTime();
  if (bt !== at) return bt - at; // yeniden eskiye
  return String(b.id ?? '').localeCompare(String(a.id ?? '')); // tie-break id DESC
}

export function priorityLabel(p: string | null | undefined): string {
  const map: Record<string, string> = { high: 'Yüksek', medium: 'Orta', low: 'Düşük', urgent: 'Acil' };
  return map[(p || '').toLowerCase()] || p || '';
}

// HTML escape — XSS koruma. innerHTML icin zorunlu.
export function escHtml(s: unknown): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Attribute icin escape — single+double quote.
export function escAttr(s: unknown): string {
  return String(s || '').replace(/'/g, '&#39;').replace(/"/g, '&quot;');
}

// Hesap TTL (saniye) → kompakt format ("3g 2s", "5s 30d", "doldu").
export function fmtTtl(sec: number | null | undefined): string {
  if (sec == null) return '';
  if (sec <= 0) return 'doldu';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  if (d > 0) return d + 'g ' + h + 's';
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return h + 's ' + m + 'd';
  return m + 'd';
}

// Default gem (agent name match yoksa fallback).
const DEFAULT_GEM: GemConfig = { name: '?', icon: '', cls: 'gem-pearl', bg: '#1e293b,#475569' };

// Agent ismi → GEMS map'inden gem config (case-insensitive substring match).
export function getGem(agentName: string = ''): GemConfig {
  for (const [key, gem] of Object.entries(GEMS)) {
    if (agentName.toLowerCase().includes(key.toLowerCase())) return gem;
  }
  return { ...DEFAULT_GEM, name: agentName || '?' };
}

// Agent payload → model rozet HTML (boş string gerek yoksa).
export function getModelBadge(agent: Pick<Agent, 'adapterConfig' | 'adapter_config'> | null | undefined): string {
  const m = agent?.adapterConfig?.model || agent?.adapter_config?.model;
  if (!m) return '';
  const label = MODEL_LABELS[m] || { short: m.replace(/^claude-/, ''), color: '#94a3b8' };
  return `<div class="ac-model" style="color:${label.color};border-color:${label.color}33;background:${label.color}14">${label.short}</div>`;
}

// ── DOM helper'lari (Faz 13) ────────────────────────────────────────
// Tip-safe getElementById + querySelector wrapper'lari. .ts modullerinde
// `document.getElementById('foo').value` pattern HTMLElement'ta hata veriyor;
// bu helper ile generic cast yapilir: `$<HTMLInputElement>('foo')?.value`.

export function $<T extends HTMLElement = HTMLElement>(id: string): T | null {
  // F4-B+ (2026-05-26): test ortamında (JSDOM cleanup sonrası async chain) document
  // undefined olabilir — guardAuth().then(...) cleanup'tan sonra fire ederse
  // unhandledRejection olur. Defensive guard ile null döner, çağıran null kontrolü zaten yapıyor.
  if (typeof document === 'undefined') return null;
  return document.getElementById(id) as T | null;
}

export function $$<T extends Element = HTMLElement>(selector: string, root: ParentNode = document): NodeListOf<T> {
  return root.querySelectorAll<T>(selector);
}

// EventTarget'i HTMLElement'a daralt (event handler'larda e.target.closest icin).
export function asEl(target: EventTarget | null): HTMLElement | null {
  return target instanceof HTMLElement ? target : null;
}
